import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core';
import {
  AppState,
  CategoryFilterOption,
  ContractTypeOption,
  FilterState,
  LivingSpaceRange,
  PriceRange,
  PropertySpaceRange,
  Region,
  ResponsiveTarget,
  RoomCountRange,
  SubcategoryOptionsMap,
  UsageSpaceRange,
} from '@immosky/types';
import { select, Store } from '@ngrx/store';
import {
  removeRegionFilter,
  resetAllFilters,
  resetRegionFilter,
  selectFilterState,
  selectIsAnyFilterActive,
  setContractTypeFilter,
  setLivingSpaceFilter,
  setMainCategoryFilter,
  setPriceFilter,
  setPropertySpaceFilter,
  setRoomCountFilter,
  setSubcategoryFilter,
  setUsageSpaceFilter,
} from '@immosky/object-search-filter';
import { Subscription } from 'rxjs';
import {
  selectIsLoadingSearchResults,
  selectIsMapVisible,
  selectIsMobileFilterOpen,
  selectResponsiveTarget,
  setIsMapVisible,
} from '@immosky/object-search-layout';
import { selectSearchResultCount } from '../../../state/results/search-results/search-results.selectors';
import { Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { SearchOverlayService } from '../../../services/search-overlay.service';
import { LayoutService } from '../../../services/layout.service';

@Component({
  selector: 'app-mobile-filters',
  templateUrl: './mobile-filters.component.html',
  styleUrls: ['./mobile-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileFiltersComponent implements OnDestroy {
  private subscriptions: Subscription[] = [];
  private responsiveTarget: ResponsiveTarget = ResponsiveTarget.XS;

  isAnyFilterActive$ = this.store.pipe(select(selectIsAnyFilterActive));
  isLoading$ = this.store.pipe(select(selectIsLoadingSearchResults));

  isOpen = false;

  regions: Region[] = [];
  filterState!: FilterState;

  isMapVisible$ = this.store.pipe(select(selectIsMapVisible));
  resultCount$ = this.store.pipe(select(selectSearchResultCount));

  displaySubcategory = false;
  displayRoomCount = false;
  displayLivingSpace = false;
  displayUsageSpace = false;
  displayPropertySpace = false;

  get scrollStrategy(): ScrollStrategy {
    return this.overlay.scrollStrategies.block();
  }

  get isCategoryActive(): boolean {
    return this.filterState.category.main.isActive;
  }

  constructor(
    private cdRef: ChangeDetectorRef,
    private store: Store<AppState>,
    private overlay: Overlay,
    private searchOverlayService: SearchOverlayService,
    private layoutService: LayoutService
  ) {
    const filterSubscription = this.store
      .pipe(select(selectFilterState))
      .subscribe((filterState: FilterState) => {
        this.filterState = filterState;
        this.regions = filterState.region.regions;

        this.displaySubcategory = filterState.category.sub.isDisplayed;
        this.displayRoomCount = filterState.roomCount.isDisplayed;
        this.displayLivingSpace = filterState.livingSpace.isDisplayed;
        this.displayUsageSpace = filterState.usageSpace.isDisplayed;
        this.displayPropertySpace = filterState.propertySpace.isDisplayed;

        this.cdRef.markForCheck();
      });

    const isOpenSubscription = this.store
      .pipe(select(selectIsMobileFilterOpen))
      .subscribe((isOpen: boolean) => {
        this.isOpen = isOpen;
        this.cdRef.markForCheck();
      });

    const responsiveTargetSubscription = this.store
      .pipe(select(selectResponsiveTarget))
      .subscribe((responsiveTarget: ResponsiveTarget) => {
        this.responsiveTarget = responsiveTarget;
        if (responsiveTarget >= ResponsiveTarget.LG) {
          this.close();
        }
      });

    this.subscriptions.push(
      filterSubscription,
      isOpenSubscription,
      responsiveTargetSubscription
    );
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  closeAndIgnoreFilterChanges() {
    this.layoutService.closeMobileFiltersAndIgnoreFilterChanges();
  }

  close() {
    this.layoutService.closeMobileFilters();
  }

  showResults() {
    this.store.dispatch(
      setIsMapVisible({
        isMapVisible: false,
      })
    );

    this.close();

    window.requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }

  resetAll() {
    this.store.dispatch(resetAllFilters());
  }

  onContractTypeChange(filter: ContractTypeOption) {
    this.store.dispatch(
      setContractTypeFilter({
        filter,
      })
    );
  }

  onCategoryChange(filter: CategoryFilterOption) {
    this.store.dispatch(
      setMainCategoryFilter({
        filter,
      })
    );
  }

  onSubcategoryChange(filter: SubcategoryOptionsMap) {
    this.store.dispatch(
      setSubcategoryFilter({
        filter,
      })
    );
  }

  onPriceChange(filter: PriceRange) {
    this.store.dispatch(
      setPriceFilter({
        filter,
      })
    );
  }

  onRoomCountChange(filter: RoomCountRange) {
    this.store.dispatch(
      setRoomCountFilter({
        filter,
      })
    );
  }

  onLivingSpaceChange(filter: LivingSpaceRange) {
    this.store.dispatch(
      setLivingSpaceFilter({
        filter,
      })
    );
  }

  onUsageSpaceChange(filter: UsageSpaceRange) {
    this.store.dispatch(
      setUsageSpaceFilter({
        filter,
      })
    );
  }

  onPropertySpaceChange(filter: PropertySpaceRange) {
    this.store.dispatch(
      setPropertySpaceFilter({
        filter,
      })
    );
  }

  trackByRegion(index: number, region: Region) {
    return region.id;
  }

  removeRegionFilter(region: Region) {
    this.store.dispatch(
      removeRegionFilter({
        region,
      })
    );
  }

  resetAllRegions() {
    this.store.dispatch(resetRegionFilter());
  }

  openMobileFilters() {
    this.layoutService.openMobileFilters();
  }

  openSearchOverlay() {
    if (this.responsiveTarget <= ResponsiveTarget.MD) {
      this.searchOverlayService.open();
    }
  }
}
